import { initializeTransferConfig } from './transferConfig';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, logEvent } from 'firebase/analytics';
import { getDatabase, Database, goOffline, goOnline } from 'firebase/database';
import { getAuth, Auth, signInAnonymously } from 'firebase/auth';
import {
    initializeAppCheck,
    ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { log, error } from '../Utils/logger';
import type { ConnectingTooLongDiagnostics } from '../Utils/connectionDiagnostics';

const firebaseConfig = {
    apiKey: 'AIzaSyAdc9yZS9H0QByAbkZbqwgxGElerC2UVQg',
    authDomain: 'qrfile-5a429.firebaseapp.com',
    databaseURL: 'https://qrfile-5a429-default-rtdb.firebaseio.com',
    projectId: 'qrfile-5a429',
    storageBucket: 'qrfile-5a429.firebasestorage.app',
    messagingSenderId: '2476216818',
    appId: '1:2476216818:web:c8767dc6b540e57eda1b6b',
    measurementId: 'G-3NTT2D9M5C',
};

type ConnectionAttemptMeta = {
    attempt_id?: string | null;
    pc_id?: string | null;
    connection_strategy?: 'primary' | 'fallback_relay';
};

type RetryOutcomeStatus = 'started' | 'success' | 'failed';

class FirebaseService {
    private app: FirebaseApp | null = null;
    private analytics: Analytics | null = null;
    private database: Database | null = null;
    private auth: Auth | null = null;
    private initPromise: Promise<void> | null = null;

    // Firestore is lazy-loaded
    private firestoreInstance: any = null;
    private firestoreFuncs: any = null;

    private ensureCore() {
        if (this.app && this.analytics && this.database && this.auth) {
            return;
        }

        this.app = initializeApp(firebaseConfig);

        if (process.env.NODE_ENV === 'development') {
            // Register the browser's generated debug token in Firebase App Check.
            (
                window as Window & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }
            ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }

        initializeAppCheck(this.app, {
            provider: new ReCaptchaEnterpriseProvider(
                '6LeT19QtAAAAADdiTOcDtFVdoF9atd4Tq4oU4ZeK',
            ),
            isTokenAutoRefreshEnabled: true,
        });

        this.analytics = getAnalytics(this.app);
        this.database = getDatabase(this.app);
        this.auth = getAuth(this.app);
        this.initPromise ??= this.signIn();
        void initializeTransferConfig(this.app);
    }

    private async getFirestore() {
        this.ensureCore();
        if (!this.firestoreInstance) {
            const { getFirestore, collection, addDoc } = await import(
                'firebase/firestore'
            );
            this.firestoreInstance = getFirestore(this.app as FirebaseApp);
            this.firestoreFuncs = { collection, addDoc };
        }
        return { db: this.firestoreInstance, ...this.firestoreFuncs };
    }

    private async signIn() {
        if (!this.auth) {
            return;
        }

        try {
            await signInAnonymously(this.auth);
            log('Firebase: Signed in anonymously');
        } catch (err) {
            error('Firebase: Anonymous sign-in failed', err);
        }
    }

    public async initialize() {
        this.ensureCore();
        return this.initPromise ?? Promise.resolve();
    }

    public getDatabase(): Database {
        this.ensureCore();
        if (!this.database) {
            throw new Error('Firebase database is not initialized');
        }
        return this.database;
    }

    public async getTurnCredentials(): Promise<{
        expiresAt: number;
        iceServers: RTCIceServer[];
    }> {
        await this.initialize();
        if (!this.app || !this.auth?.currentUser) {
            throw new Error(
                'Sign-in is required to connect. Please refresh and retry.',
            );
        }
        const { getFunctions, httpsCallable } = await import(
            'firebase/functions'
        );
        const getCredentials = httpsCallable<
            undefined,
            {
                expiresAt: number;
                iceServers: RTCIceServer[];
            }
        >(getFunctions(this.app, 'us-central1'), 'getTurnCredentials', {
            timeout: 15000,
        });
        const { data } = await getCredentials();
        if (
            !Number.isFinite(data.expiresAt) ||
            data.expiresAt * 1000 <= Date.now() + 60000 ||
            !Array.isArray(data.iceServers) ||
            data.iceServers.length === 0
        ) {
            throw new Error(
                'Unable to obtain valid connection credentials. Please retry.',
            );
        }
        return data;
    }

    public async serverTransfer<T>(data: Record<string, unknown>): Promise<T> {
        await this.initialize();
        const { getFunctions, httpsCallable } = await import(
            'firebase/functions'
        );
        const call = httpsCallable<Record<string, unknown>, T>(
            getFunctions(this.app!, 'us-central1'),
            'serverTransfer',
            { timeout: 60000 },
        );
        return (await call(data)).data;
    }

    public async goOffline() {
        await this.initialize();
        log('Firebase: Going offline');
        goOffline(this.getDatabase());
    }

    public async goOnline() {
        await this.initialize();
        log('Firebase: Going online');
        goOnline(this.getDatabase());
    }

    public async logEvent(eventName: string, params?: { [key: string]: any }) {
        const consent = localStorage.getItem('cookie-consent');
        if (consent !== 'accepted') return;

        await this.initialize();
        if (!this.analytics) {
            return;
        }
        logEvent(this.analytics, eventName, params);
    }

    public async sendRating(data: {
        is_sender: boolean;
        rating: number;
        ua: string;
        session_id: string;
    }) {
        await this.initialize();
        try {
            log('Firebase: Sending rating info to Firestore', data);
            const { db, collection, addDoc } = await this.getFirestore();

            const ratingRef = collection(db, 'ratings');
            await addDoc(ratingRef, {
                ...data,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: Rating sent successfully');
        } catch (err) {
            error('Firebase: Failed to send rating', err);
        }
    }

    public async sendFeedback(feedback: {
        files_count: number;
        is_sender: boolean;
        rating: number;
        text: string;
        ua?: string;
        session_id: string;
    }) {
        await this.initialize();
        try {
            log('Firebase: Sending feedback to Firestore', feedback);
            const { db, collection, addDoc } = await this.getFirestore();

            const feedbackRef = collection(db, 'feedback');
            await addDoc(feedbackRef, {
                ...feedback,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: Feedback sent successfully');
        } catch (err) {
            error('Firebase: Failed to send feedback', err);
        }
    }

    public async logSessionStart(sessionId: string) {
        await this.initialize();
        try {
            log('Firebase: Logging session start', sessionId);
            const { db, collection, addDoc } = await this.getFirestore();

            const startRef = collection(db, 'start');
            await addDoc(startRef, {
                session_id: sessionId,
                ua: navigator.userAgent,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: Session start logged successfully');
        } catch (err) {
            error('Firebase: Failed to log session start', err);
        }
    }

    public async logConnecting(
        sessionId: string,
        meta?: ConnectionAttemptMeta,
    ) {
        await this.initialize();
        try {
            log('Firebase: Logging connecting attempt', sessionId);
            const { db, collection, addDoc } = await this.getFirestore();

            const connectingRef = collection(db, 'connecting');
            await addDoc(connectingRef, {
                session_id: sessionId,
                attempt_id: meta?.attempt_id ?? null,
                pc_id: meta?.pc_id ?? null,
                connection_strategy: meta?.connection_strategy ?? 'primary',
                ua: navigator.userAgent,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: Connecting attempt logged successfully');
        } catch (err) {
            error('Firebase: Failed to log connecting attempt', err);
        }
    }

    public async logConnected(
        sessionId: string,
        timeToConnectMs: number,
        meta?: ConnectionAttemptMeta,
    ) {
        await this.initialize();
        try {
            log(
                'Firebase: Logging successful connection',
                sessionId,
                timeToConnectMs,
            );
            const { db, collection, addDoc } = await this.getFirestore();

            const connectedRef = collection(db, 'connected');
            await addDoc(connectedRef, {
                session_id: sessionId,
                attempt_id: meta?.attempt_id ?? null,
                pc_id: meta?.pc_id ?? null,
                connection_strategy: meta?.connection_strategy ?? 'primary',
                ua: navigator.userAgent,
                time_to_connect_ms: timeToConnectMs,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: Successful connection logged');
        } catch (err) {
            error('Firebase: Failed to log successful connection', err);
        }
    }

    public async logConnectingTooLong(
        sessionId: string,
        diagnostics: ConnectingTooLongDiagnostics,
        meta?: ConnectionAttemptMeta,
    ) {
        await this.initialize();
        try {
            log(
                'Firebase: Logging connecting_too_long',
                sessionId,
                diagnostics,
            );
            const { db, collection, addDoc } = await this.getFirestore();

            const tooLongRef = collection(db, 'connecting_too_long');
            await addDoc(tooLongRef, {
                session_id: sessionId,
                attempt_id: meta?.attempt_id ?? null,
                pc_id: meta?.pc_id ?? null,
                connection_strategy: meta?.connection_strategy ?? 'primary',
                ua: navigator.userAgent,
                ...diagnostics,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: connecting_too_long logged successfully');
        } catch (err) {
            error('Firebase: Failed to log connecting_too_long', err);
        }
    }

    public async logConnectionRetryOutcome(data: {
        session_id: string;
        status: RetryOutcomeStatus;
        strategy: 'fallback_relay';
        source_attempt_id?: string | null;
        source_pc_id?: string | null;
        retry_attempt_id?: string | null;
        retry_pc_id?: string | null;
        elapsed_ms?: number | null;
        reason?: string | null;
        details?: Record<string, unknown>;
    }) {
        await this.initialize();
        try {
            const { db, collection, addDoc } = await this.getFirestore();
            const retryRef = collection(db, 'connection_retry_outcome');
            await addDoc(retryRef, {
                ...data,
                ua: navigator.userAgent,
                timestamp: new Date().toISOString(),
            });
        } catch (err) {
            error('Firebase: Failed to log connection retry outcome', err);
        }
    }

    public async logConnectionRetryRecovered(data: {
        session_id: string;
        source_failure_reason:
            | 'connecting_too_long'
            | 'connection_state_failed';
        source_attempt_id?: string | null;
        source_pc_id?: string | null;
        retry_attempt_id?: string | null;
        retry_pc_id?: string | null;
        retry_strategy: 'fallback_relay';
        retry_time_to_connect_ms: number;
    }) {
        await this.initialize();
        try {
            const { db, collection, addDoc } = await this.getFirestore();
            const recoveredRef = collection(db, 'connection_retry_recovered');
            await addDoc(recoveredRef, {
                ...data,
                ua: navigator.userAgent,
                timestamp: new Date().toISOString(),
            });
        } catch (err) {
            error('Firebase: Failed to log connection retry recovered', err);
        }
    }

    public async logFileTransfer(data: {
        ext: string;
        is_sender: boolean;
        is_sent: boolean;
        session_id: string;
        size: number;
        time_to_send_ms: number;
        transport?: 'p2p' | 'server';
    }) {
        if (data.time_to_send_ms < 45000) {
            log(
                'Firebase: Skipping file transfer log (transfer took less than 45s)',
            );
            return;
        }

        await this.initialize();
        try {
            log('Firebase: Logging file transfer', data);
            const { db, collection, addDoc } = await this.getFirestore();

            const filesRef = collection(db, 'files');
            await addDoc(filesRef, {
                ...data,
                ua: navigator.userAgent,
                timestamp: new Date().toISOString(),
            });

            log('Firebase: File transfer logged successfully');
        } catch (err) {
            error('Firebase: Failed to log file transfer', err);
        }
    }

    private reportedFirstFileTransfer: boolean = false;

    public async logFirstFileTransfered(
        sessionId: string,
        fileExt: string,
        size: number,
        timeMs: number,
        serverTransfer = false,
    ) {
        if (this.reportedFirstFileTransfer) return;
        this.reportedFirstFileTransfer = true;

        try {
            await this.logEvent('first_file_transfered', {
                session_id: sessionId,
                file_ext: fileExt,
                size,
                time_ms: timeMs,
                ...(serverTransfer ? { server_transfer: true } : {}),
            });
        } catch (err) {
            error('Firebase: Failed to log first file transfered event', err);
            this.reportedFirstFileTransfer = false;
        }
    }
}

const firebaseService = new FirebaseService();
export default firebaseService;
